import { type RefObject, useEffect, useRef } from 'react'

type UseFollowNewestOnUpdate = (params: {
  scrollRef: RefObject<HTMLElement | null>
  /** Id of the currently-newest item (typically `items[0]?.id` when sorted newest-first). */
  newestId: string | undefined
  /** Skip the follow until initial scroll has settled. */
  enabled: boolean
  /** Read at the moment a new newest arrives — if false, scroll is left alone. */
  isAtBottomRef: RefObject<boolean>
}) => void

/**
 * Pins the scroll container to the bottom for ~1s whenever `newestId` changes,
 * but only if the user was at the bottom at the moment of the change.
 *
 * The 1s pin window absorbs any async growth of the newly arrived row (e.g.
 * a freshly mounted chat message whose decrypted text fills in over several
 * frames). Bails on user scroll up.
 */
export const useFollowNewestOnUpdate: UseFollowNewestOnUpdate = ({
  scrollRef,
  newestId,
  enabled,
  isAtBottomRef,
}) => {
  const prevNewestIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!enabled) {
      return
    }
    const prev = prevNewestIdRef.current
    prevNewestIdRef.current = newestId
    if (!prev || !newestId || newestId === prev) {
      return
    }
    if (!isAtBottomRef.current) {
      return
    }
    const el = scrollRef.current
    if (!el) {
      return
    }

    const FRAMES = 60 // ~1s
    let framesLeft = FRAMES
    let lastWrittenScrollTop: number | null = null
    let rafId: number | null = null

    const tick = () => {
      if (lastWrittenScrollTop !== null && el.scrollTop < lastWrittenScrollTop - 5) {
        rafId = null
        return
      }
      el.scrollTop = el.scrollHeight
      lastWrittenScrollTop = el.scrollTop
      framesLeft--
      if (framesLeft > 0) {
        rafId = requestAnimationFrame(tick)
      } else {
        rafId = null
      }
    }
    rafId = requestAnimationFrame(tick)
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [newestId, enabled, scrollRef, isAtBottomRef])
}
